import { toaster } from "@/components/ui/toaster";
import {
  createSlotRequest,
  CreateSlotRequestProps,
  getAssignedSlotsInRequest,
  getSlotRequest,
  getSlotRequests,
  GetSlotRequestsProps,
  updateSlotRequest,
  UpdateSlotRequestProps,
} from "@/services/slot-requests.service";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, RefObject, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import {
  SlotMainData,
  SlotRequestDetail,
  SlotRequestListResponse,
  SlotSampleData,
} from "./slot-requests.schema";
import { getCuentasByBusqueda } from "@/services/accounts.service";
import { Option } from "@/components/select/AsyncAutocompleteSelect";
import { AppRoutes } from "@/constants/routes";
import { getSlotRequestsFilter, SRFRProps } from "./filter-obj";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { EstadosCupos } from "@/constants/enums/status/entity-status-ids";

export interface FetchSlotRequestsHandlerProps {
  setLoadingSlotRequests: Dispatch<SetStateAction<boolean>>;
  filters?: GetSlotRequestsProps;
  showLoad?: boolean;
  setSlotRequests: Dispatch<
    SetStateAction<SlotRequestListResponse | undefined>
  >;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchSlotRequestsHandler = async ({
  setLoadingSlotRequests,
  filters,
  showLoad,
  setSlotRequests,
  navigate,
  fetchPermissions,
}: FetchSlotRequestsHandlerProps) => {
  try {
    if (showLoad) setLoadingSlotRequests(true);

    const response = await getSlotRequests({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setSlotRequests(response);

    setLoadingSlotRequests(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo obtener las solicitudes de granos a entregar.",
      });
    }
    throw error;
  } finally {
    setLoadingSlotRequests(false);
  }
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("slot_request_date", undefined);
  setValue("account_number", undefined);
  setValue("estados", []);
  setValue("grain", []);
  setValue("origin", "");
  setValue("direct_slots", "");
  setValue("plant_slots", "");

  submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<
    SetStateAction<Partial<GetSlotRequestsProps> | undefined>
  >;
  fetchSlotRequests: (filters?: any) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchSlotRequests,
  setOpenFilters,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetSlotRequestsProps> = getSlotRequestsFilter(
    event as SRFRProps,
  );

  setCurrentFilters(filterObj);

  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  await fetchSlotRequests(filterObj);

  setOpenFilters(false);
};

export interface GetAccountOptionsHandlerProps {
  inputValue: string;
}

export const getOptionsHandler = async ({
  inputValue,
}: GetAccountOptionsHandlerProps) => {
  const filtered = await getCuentasByBusqueda({
    busqueda: inputValue,
  });

  return (
    filtered?.map((p) => ({
      label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,
      value: p.nroCuenta,
      tipoCuenta: p.tipoCuenta,
      idTipoCuenta: p.idTipoCuenta,
    })) ?? []
  );
};

export const loadOptionsHandler = ({
  timeoutRef,
  inputValue,
  getOptions,
  setLoadingPersons,
}: LoadOptionsHandlerProps): Promise<Option[]> => {
  return new Promise((resolve) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef?.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (inputValue.trim().length < 3) {
        resolve([]);
        return;
      }

      setLoadingPersons(true);
      const options = getOptions(inputValue);

      setLoadingPersons(false);
      resolve(options);
    }, 1000);
  });
};

export interface LoadOptionsHandlerProps {
  timeoutRef: RefObject<NodeJS.Timeout | null>;
  inputValue: string;
  setLoadingPersons: Dispatch<SetStateAction<boolean>>;
  getOptions: (inputValue: string) => Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  >;
}

export interface CreateSlotRequestHandlerProps {
  navigate: NavigateFunction;
  setLoadingCreateSlotRequest: Dispatch<SetStateAction<boolean>>;
  watchedValues: any;
  fetchPermissions: () => Promise<void>;
}

export const createSlotRequestHandler = async ({
  navigate,
  setLoadingCreateSlotRequest,
  fetchPermissions,
  watchedValues,
}: CreateSlotRequestHandlerProps) => {
  setLoadingCreateSlotRequest(true);

  try {
    const objToSend: CreateSlotRequestProps = {
      nroCuentaOrigen: watchedValues?.account?.value,
      grano: watchedValues?.grain,
      cosecha: watchedValues?.harvest?.[0],
      mensaje: watchedValues?.order_detail,
      procedencia: watchedValues?.origin_place,
      cuposAPlanta: watchedValues?.plant_slots,
      cuposDirectos: watchedValues?.direct_slots,
    };

    await createSlotRequest(objToSend);

    toaster.create({
      title: "Solicitud de cupos creada con éxito",
      type: "success",
    });
    setLoadingCreateSlotRequest(false);
    navigate(AppRoutes.SLOT_REQUESTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SLOT_REQUESTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorMsj ?? "No se ha podido crear la venta de granos a futuro .",
        duration: 6000,
      });
    }
    throw error;
  } finally {
    setLoadingCreateSlotRequest(false);
  }
};

export interface HandleRequestInformationHandlerProps {
  setLoadingRequestInformation: Dispatch<SetStateAction<boolean>>;
  event?: FieldValues;
  slotRequest?: SlotRequestDetail | null;
  fetchSlotRequest: (showLoad?: boolean) => Promise<void>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setOpenInformationModal: Dispatch<SetStateAction<boolean>>;
}

export const handleSendSlotRequestInformationHandler = async ({
  setLoadingRequestInformation,
  event,
  slotRequest,
  fetchSlotRequest,
  navigate,
  fetchPermissions,
  setOpenInformationModal,
}: HandleRequestInformationHandlerProps) => {
  setLoadingRequestInformation(true);
  try {
    const id = Number(slotRequest?.id);
    const objToSend: UpdateSlotRequestProps = {
      estado: EstadosCupos.RequiereInformacion,
      mensaje: event?.request_detail,
    };

    await updateSlotRequest(id, objToSend);

    toaster.create({
      type: "success",
      title: "Pedido de información realizado exitosamente",
    });

    await fetchSlotRequest(false);
    setLoadingRequestInformation(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "Hubo un error al enviar el pedido de información.",
      });
      throw error;
    }
  } finally {
    setLoadingRequestInformation(false);
  }

  setOpenInformationModal(false);
};

export interface HandleRejectSlotRequestHandlerProps {
  event?: FieldValues;
  setLoadingRejectSlotRequest: Dispatch<SetStateAction<boolean>>;
  slotRequest?: SlotRequestDetail | null;
  navigate: NavigateFunction;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  fetchPermissions: () => Promise<void>;
}
export const handleRejectSlotRequestHandler = async ({
  setLoadingRejectSlotRequest,

  event,
  slotRequest,
  navigate,
  setOpenRejectModal,
  fetchPermissions,
}: HandleRejectSlotRequestHandlerProps) => {
  setLoadingRejectSlotRequest(true);
  try {
    const id = Number(slotRequest?.id);
    const objToSend: UpdateSlotRequestProps = {
      mensaje: event?.request_detail,
      estado: EstadosCupos.Rechazada
    };

    await updateSlotRequest(id, objToSend);

    toaster.create({
      type: "success",
      title: `Solicitud de cupos rechazada exitosamente.`,
    });

    setLoadingRejectSlotRequest(false);

    navigate(AppRoutes.SLOT_REQUESTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMsj ?? "No se pudo rechazar la solicitud de cupos.",
      });
      throw error;
    }
  } finally {
    setLoadingRejectSlotRequest(false);
  }

  setOpenRejectModal(false);
};

export interface HandleAssignSlotRequestProps {
  setLoadingAssignSlotRequest: Dispatch<SetStateAction<boolean>>;
  slotRequest?: SlotRequestDetail | null;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  fetchSlotRequest: (showLoad?: boolean) => Promise<void>;
  setOpenAssignSlotsModal: Dispatch<SetStateAction<boolean>>;
  selection: SlotSampleData[];
}

export const handleAssignSlotsHandler = async ({
  setLoadingAssignSlotRequest,
  navigate,
  slotRequest,
  fetchPermissions,
  fetchSlotRequest,
  setOpenAssignSlotsModal,
  selection,
}: HandleAssignSlotRequestProps) => {
  setLoadingAssignSlotRequest(true);
  try {
    //Tendríamos que usar la cantidad de cupos que se envía y comparar con el total de cupos de la solicitud

    const id = slotRequest?.id || 0;
    const objToSend: UpdateSlotRequestProps = {
      estado: EstadosCupos.CuposAsignados,
      cuposAAsignar: selection.map((e) => e.id),
    };

    await updateSlotRequest(id, objToSend);

    toaster.create({
      type: "success",
      title: `Cupos asignados exitosamente.`,
    });

    await fetchSlotRequest(false);

    setLoadingAssignSlotRequest(false);
    setOpenAssignSlotsModal(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron asignar los cupos",
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  } finally {
    setLoadingAssignSlotRequest(false);
  }
};

export interface FetchSlotRequestHandlerProps {
  showLoad: boolean;
  id: number;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoadingSlotRequest: Dispatch<SetStateAction<boolean>>;
  setSlotRequest: Dispatch<SetStateAction<SlotRequestDetail | undefined>>;
}

export const fetchSlotRequestHandler = async ({
  fetchPermissions,
  id,
  navigate,
  setLoadingSlotRequest,
  showLoad,
  setSlotRequest,
}: FetchSlotRequestHandlerProps) => {
  try {
    if (showLoad) setLoadingSlotRequest(true);

    const response = await getSlotRequest({
      id: Number(id),
    });

    setSlotRequest(response);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: `No se pudo traer los datos de la solicitud.`,
      });
    }
    throw error;
  } finally {
    setLoadingSlotRequest(false);
  }
};

export interface FetchAssignedSlotsHandlerProps {
  setAssignedSlots: Dispatch<SetStateAction<SlotMainData[] | undefined>>;
  slotRequest?: SlotRequestDetail;
}

export const fetchAssignedSlotsHandler = async ({
  slotRequest,
  setAssignedSlots,
}: FetchAssignedSlotsHandlerProps) => {
  if (!slotRequest?.id) return;
  const response = await getAssignedSlotsInRequest({
    id: slotRequest?.id,
  });

  setAssignedSlots(response);
};
